import { ChangeEvent, KeyboardEvent, MouseEvent, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  errorSnackbar,
  successSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { updateMachineUseCase } from "@src/cloud-connect/client-configuration/use-cases/update-machine-use-case";
import { findSearchedMachinesList } from "@src/components/organisms/machines-list-search/machines-list-search";
import { MachinesListCompanyEntry } from "@src/types/gateways-with-machines";
import {
  setAllMachinesWithGatewaysListData,
  setConnectedGatewaysWithMachinesListData,
  setSearchMachinesWithGatewaysListData,
} from "@src/reducers/gateways-machines-slice";
import { RootState } from "@src/reducers/store";
import { useI18N } from "@src/language/language-context";

export interface EditableMachineName {
  machine_id: string;
  name: string;
}

export const updateMachineNameInCompanyData = (
  companyData: Record<string, MachinesListCompanyEntry>,
  machineId: string,
  name: string,
): Record<string, MachinesListCompanyEntry> =>
  Object.fromEntries(
    Object.entries(companyData).map(([companyKey, company]) => {
      const machine = company.machines[machineId];
      return [
        companyKey,
        machine
          ? {
              ...company,
              machines: {
                ...company.machines,
                [machineId]: { ...machine, name },
              },
            }
          : company,
      ];
    }),
  );

export const useEditMachineNameHook = (machine: EditableMachineName) => {
  const dispatch = useDispatch();
  const i18n = useI18N();
  const allMachinesListData = useSelector(
    (state: RootState) => state.gatewaysMachinesSlice.allMachinesListData,
  );
  const machinesManagerSearchTerms = useSelector(
    (state: RootState) =>
      state.gatewaysMachinesSlice.machinesManagerSearchTerms,
  );
  const connectedGatewaysListData = useSelector(
    (state: RootState) => state.gatewaysMachinesSlice.connectedGatewaysListData,
  );
  const [showInput, setShowInput] = useState(false);
  const [newMachineName, setNewMachineName] = useState(machine.name);
  const [isSaving, setIsSaving] = useState(false);

  const texts = {
    errorInputEmpty: i18n("modules.editSourceName.errorInputEmpty"),
    errorUpdateFailed: i18n("modules.editSourceName.errorUpdateFailed"),
    successfullyUpdated: i18n("modules.editSourceName.successfullyUpdated"),
    save: i18n("modules.editSourceName.save"),
    edit: i18n("modules.editSourceName.edit"),
    cancel: i18n("modules.editSourceName.cancel"),
  };

  const editMachineName = (event: MouseEvent) => {
    event.stopPropagation();
    setShowInput(true);
  };

  const updateNewMachineName = (event: ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    setNewMachineName(event.target.value);
  };

  const cancelEdit = (event: MouseEvent) => {
    event.stopPropagation();
    setNewMachineName(machine.name);
    setShowInput(false);
  };

  const saveMachineName = async (event: MouseEvent | KeyboardEvent) => {
    event.stopPropagation();
    if (isSaving) return;

    const name = newMachineName.trim();
    if (!name) {
      errorSnackbar(texts.errorInputEmpty);
      return;
    }

    setIsSaving(true);
    const result = await updateMachineUseCase(machine.machine_id, { name });
    if (!result.success) {
      errorSnackbar(result.error || texts.errorUpdateFailed, 5000);
      setNewMachineName(machine.name);
      setIsSaving(false);
      return;
    }

    const updatedAllMachinesListData = updateMachineNameInCompanyData(
      allMachinesListData,
      machine.machine_id,
      name,
    );
    const updatedSearchMachinesListData = machinesManagerSearchTerms.length
      ? findSearchedMachinesList(
          updatedAllMachinesListData,
          machinesManagerSearchTerms,
        )
      : {};

    dispatch(setAllMachinesWithGatewaysListData(updatedAllMachinesListData));
    dispatch(
      setSearchMachinesWithGatewaysListData(updatedSearchMachinesListData),
    );
    dispatch(
      setConnectedGatewaysWithMachinesListData(
        connectedGatewaysListData.map((gateway) => ({
          ...gateway,
          machines: gateway.machines.map((entry) =>
            entry.machine_id === machine.machine_id
              ? { ...entry, name }
              : entry,
          ),
        })),
      ),
    );
    successSnackbar(texts.successfullyUpdated, 5000);
    setNewMachineName(name);
    setShowInput(false);
    setIsSaving(false);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    event.stopPropagation();
    if (event.key === "Enter") {
      event.preventDefault();
      void saveMachineName(event);
    }
  };

  return {
    showInput,
    newMachineName,
    isSaving,
    texts,
    editMachineName,
    updateNewMachineName,
    saveMachineName,
    cancelEdit,
    onKeyDown,
  };
};
